/**
 * Slug rules (single source of truth, used by the client AND the meta server).
 *
 * These rules exist specifically to make the audited URL bugs impossible:
 *  - B3/B4: article slugs that contained `?`, spaces, `:`, `...`, commas or raw
 *    non-Latin (Telugu) text produced unreachable URLs. `slugify` can only ever
 *    emit `[a-z0-9-]`, is never empty, and is never over-length.
 *
 * A slug is ALWAYS: /^[a-z0-9]+(?:-[a-z0-9]+)*$/ and 1..MAX_SLUG_LENGTH chars.
 */

export const MAX_SLUG_LENGTH = 80;
export const SLUG_PATTERN = /^[a-z0-9]+(?:-[a-z0-9]+)*$/;

/** Deterministic, stable short hash (djb2 -> base36). */
export function shortHash(input) {
  let h = 5381;
  const s = String(input);
  for (let i = 0; i < s.length; i++) h = ((h << 5) + h + s.charCodeAt(i)) >>> 0;
  return h.toString(36).slice(0, 6).padStart(4, '0');
}

function trimToWord(s, max) {
  if (s.length <= max) return s;
  const cut = s.slice(0, max);
  const at = cut.lastIndexOf('-');
  return (at > max * 0.5 ? cut.slice(0, at) : cut).replace(/-+$/, '');
}

/**
 * Produce a guaranteed-safe slug.
 * @param {string} input
 * @param {{ fallback?: string, latin?: string, maxLength?: number }} [opts]
 *   `latin` is an optional already-Latin hint used when `input` has no
 *   ASCII-representable characters (e.g. a Telugu title with an English title).
 */
export function slugify(input, opts = {}) {
  const fallback = opts.fallback || 'item';
  const maxLength = opts.maxLength || MAX_SLUG_LENGTH;

  const build = (raw) =>
    String(raw ?? '')
      .normalize('NFKD')
      .replace(/[\u0300-\u036f]/g, '')
      .toLowerCase()
      .replace(/['\u2018\u2019"]/g, '')
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/-{2,}/g, '-')
      .replace(/^-+|-+$/g, '');

  let s = build(input);
  if (!s && opts.latin) s = build(opts.latin);
  if (s.length > maxLength) s = trimToWord(s, maxLength);
  if (!s) s = `${fallback}-${shortHash(String(input ?? ''))}`;
  return s;
}

/** Strict validation used at form-submit time and by the server before insert. */
export function isValidSlug(slug) {
  return (
    typeof slug === 'string' &&
    slug.length > 0 &&
    slug.length <= MAX_SLUG_LENGTH &&
    SLUG_PATTERN.test(slug)
  );
}

/**
 * Derive a guaranteed-safe, unique slug from a title or an explicit slug.
 * Shared so the editor preview, the HTTP API and the repository all agree.
 *
 * @param {string} title
 * @param {string|undefined} explicit
 * @param {string[]} taken
 */
export function resolveSlug(title, explicit, taken) {
  // No `latin` hint here on purpose: for a title with no ASCII characters the
  // fallback path appends a stable hash (`article-1xi5zm`), which stays unique
  // and recognisable. Passing a Latin word instead would collapse every
  // non-Latin title onto the same bare slug.
  const base = slugify(explicit && explicit.trim() ? explicit : title, { fallback: 'article' });
  return uniqueSlug(base, taken);
}

/**
 * Make a slug unique against a set of taken slugs (case-insensitive).
 * Deterministic: base, base-2, base-3, ...
 */
export function uniqueSlug(base, taken) {
  const take = new Set([...taken].map((s) => String(s).toLowerCase()));
  let candidate = base;
  let n = 2;
  while (take.has(candidate.toLowerCase())) {
    const suffix = `-${n++}`;
    candidate = `${base.slice(0, MAX_SLUG_LENGTH - suffix.length)}${suffix}`;
  }
  return candidate;
}
